/**
 * PlaybookRuleForm — modal for creating/editing a playbook rule.
 *
 * Fields: category, rule name, standard position, risk description,
 * severity, applies-to, enabled toggle.
 *
 * Requirements: 8.1, 8.2, 8.6
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import type { PlaybookRule } from '@/types';
import type { CreatePlaybookRuleInput, UpdatePlaybookRuleInput } from '@/services/playbookService';

const SEVERITIES = ['High', 'Medium', 'Low'] as const;

export interface PlaybookRuleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (input: CreatePlaybookRuleInput | UpdatePlaybookRuleInput) => void;
  /** When provided the form is in edit mode. */
  rule?: PlaybookRule | null;
  /** The 8 predefined categories. */
  categories: string[];
  isPending?: boolean;
}

export function PlaybookRuleForm({
  isOpen,
  onClose,
  onSave,
  rule,
  categories,
  isPending = false,
}: PlaybookRuleFormProps) {
  const isEdit = !!rule;

  const [form, setForm] = useState({
    category: '',
    ruleName: '',
    standardPosition: '',
    riskDescription: '',
    severity: 'Medium',
    appliesTo: '',
    isEnabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    if (rule) {
      setForm({
        category: rule.category,
        ruleName: rule.ruleName,
        standardPosition: rule.standardPosition,
        riskDescription: rule.riskDescription ?? '',
        severity: rule.severity,
        appliesTo: rule.appliesTo ?? '',
        isEnabled: rule.isEnabled,
      });
    } else {
      setForm({
        category: categories[0] ?? '',
        ruleName: '',
        standardPosition: '',
        riskDescription: '',
        severity: 'Medium',
        appliesTo: '',
        isEnabled: true,
      });
    }
    setErrors({});
  }, [rule, isOpen, categories]);

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.category) newErrors.category = 'Category is required.';
    if (!form.ruleName.trim()) newErrors.ruleName = 'Rule name is required.';
    if (!form.standardPosition.trim()) newErrors.standardPosition = 'Standard position is required.';
    if (!form.severity) newErrors.severity = 'Severity is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const input: CreatePlaybookRuleInput = {
      category: form.category,
      ruleName: form.ruleName.trim(),
      standardPosition: form.standardPosition.trim(),
      riskDescription: form.riskDescription.trim() || null,
      severity: form.severity,
      isEnabled: form.isEnabled,
      appliesTo: form.appliesTo.trim() || null,
    };

    onSave(input);
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Playbook Rule' : 'New Playbook Rule'}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.4rem 1rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="playbook-rule-form"
            disabled={isPending}
            style={{
              padding: '0.4rem 1rem',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#c8102e',
              color: '#fff',
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {isPending ? 'Saving…' : isEdit ? 'Update Rule' : 'Create Rule'}
          </button>
        </>
      }
    >
      <form id="playbook-rule-form" onSubmit={handleSubmit}>
        {/* Category */}
        <FormField label="Category" error={errors.category}>
          <select
            id="field-category"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            aria-invalid={!!errors.category}
            style={{
              width: '100%',
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: `1px solid ${errors.category ? '#ef4444' : '#d1d5db'}`,
              fontSize: '0.875rem',
            }}
          >
            <option value="">Select category…</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </FormField>

        {/* Rule Name */}
        <FormField
          label="Rule Name"
          value={form.ruleName}
          onChange={(e) => setForm((f) => ({ ...f, ruleName: e.target.value }))}
          error={errors.ruleName}
          required
          maxLength={255}
          placeholder="e.g., Auto-Renewal Cap"
        />

        {/* Standard Position */}
        <FormField label="Standard Position" error={errors.standardPosition}>
          <textarea
            id="field-standard-position"
            value={form.standardPosition}
            onChange={(e) => setForm((f) => ({ ...f, standardPosition: e.target.value }))}
            rows={3}
            required
            aria-invalid={!!errors.standardPosition}
            placeholder="Baker's standard contractual position for this rule…"
            style={{
              width: '100%',
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: `1px solid ${errors.standardPosition ? '#ef4444' : '#d1d5db'}`,
              fontSize: '0.875rem',
              resize: 'vertical',
            }}
          />
        </FormField>

        {/* Risk Description */}
        <FormField label="Risk Description">
          <textarea
            id="field-risk-description"
            value={form.riskDescription}
            onChange={(e) => setForm((f) => ({ ...f, riskDescription: e.target.value }))}
            rows={2}
            placeholder="Describe the risk if this position is not met…"
            style={{
              width: '100%',
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              fontSize: '0.875rem',
              resize: 'vertical',
            }}
          />
        </FormField>

        {/* Severity + Applies To row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          <FormField label="Severity" error={errors.severity}>
            <select
              id="field-severity"
              value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
              aria-invalid={!!errors.severity}
              style={{
                width: '100%',
                padding: '0.4rem 0.75rem',
                borderRadius: '4px',
                border: `1px solid ${errors.severity ? '#ef4444' : '#d1d5db'}`,
                fontSize: '0.875rem',
              }}
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Applies To"
            value={form.appliesTo}
            onChange={(e) => setForm((f) => ({ ...f, appliesTo: e.target.value }))}
            placeholder="e.g., MSA, SOW"
            maxLength={255}
          />
        </div>

        {/* Enabled toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
          <input
            id="field-enabled"
            type="checkbox"
            checked={form.isEnabled}
            onChange={(e) => setForm((f) => ({ ...f, isEnabled: e.target.checked }))}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <label htmlFor="field-enabled" style={{ fontSize: '0.875rem', color: '#334155', cursor: 'pointer' }}>
            Rule enabled
          </label>
        </div>
      </form>
    </Modal>
  );
}
